import { i18next } from './i18n';

const currentLocale = (): string => i18next.language || 'en';

const EM_DASH = '—';

/** JOD, always. The tenant currency is a column, but MVP is one currency (plan §3.2). */
export const CURRENCY = 'JOD';

/** Money is stored to 0.001 JOD; lists round to whole dinars, detail views show the exact figure. */
export const MONEY_FRACTION_DIGITS = 3;

const isBlank = (value: number | string | null | undefined): boolean =>
  value === null || value === undefined || value === '' || (typeof value === 'number' && Number.isNaN(value));

/** Short calendar date in the active locale, e.g. `17 Aug 2026`. */
export const formatDay = (value: string | number | Date | null | undefined): string => {
  if (isBlank(value as number | string | null | undefined)) {
    return EM_DASH;
  }

  const date = value instanceof Date ? value : new Date(value as string | number);

  if (Number.isNaN(date.getTime())) {
    return EM_DASH;
  }

  return new Intl.DateTimeFormat(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};

/** Date and time in the active locale, used for `lastChecked`-style timestamps. */
export const formatDayTime = (value: string | number | Date | null | undefined): string => {
  if (isBlank(value as number | string | null | undefined)) {
    return EM_DASH;
  }

  const date = value instanceof Date ? value : new Date(value as string | number);

  if (Number.isNaN(date.getTime())) {
    return EM_DASH;
  }

  return new Intl.DateTimeFormat(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export interface FormatMoneyOptions {
  /**
   * `false` (the default) rounds to whole dinars — what a list cell, a StatCard and an offer show.
   * `true` keeps all three decimals, for the valuation breakdown and the cost rows where the 0.001 is
   * the point.
   */
  exact?: boolean;
  /** Drop the `JOD` prefix when the column header already carries it. */
  withoutCurrency?: boolean;
}

/**
 * An amount in JOD. Digits stay latin and LTR even in Arabic — a price is read as a number,
 * not as prose — and `tabular-nums` in the component keeps columns aligned (plan §2.7).
 */
export const formatMoney = (value: number | null | undefined, { exact = false, withoutCurrency = false }: FormatMoneyOptions = {}): string => {
  if (isBlank(value)) {
    return EM_DASH;
  }

  const digits = exact ? MONEY_FRACTION_DIGITS : 0;

  const amount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: exact ? digits : 0,
    maximumFractionDigits: digits,
  }).format(value as number);

  return withoutCurrency ? amount : `${CURRENCY} ${amount}`;
};

/**
 * A stored fraction rendered as a percentage — `0.175` → `17.5%` (plan §11.6).
 *
 * Pinned to `en-US` for the same reason `formatMoney` is: a figure is read as a number, not as prose,
 * and an Arabic Costs tab that printed `JOD 20,800` beside `١٧٫٥٪` was mixing two numeral systems in
 * one list (plan §2.7).
 */
export const formatPercent = (fraction: number | null | undefined, maximumFractionDigits = 1): string => {
  if (isBlank(fraction)) {
    return EM_DASH;
  }

  return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits }).format(fraction as number);
};

/** A duration in whole days — days in stock, days since an offer was shared. */
export const formatDays = (value: number | null | undefined): string => {
  if (isBlank(value)) {
    return EM_DASH;
  }

  return new Intl.NumberFormat(currentLocale(), { style: 'unit', unit: 'day', unitDisplay: 'long', maximumFractionDigits: 0 }).format(
    value as number,
  );
};

/** A distance with a localized `km` unit, e.g. `42,000 km`. */
export const formatKm = (value: number | null | undefined, maximumFractionDigits = 0): string => {
  if (isBlank(value)) {
    return EM_DASH;
  }

  return new Intl.NumberFormat(currentLocale(), {
    style: 'unit',
    unit: 'kilometer',
    unitDisplay: 'short',
    maximumFractionDigits,
  }).format(value as number);
};

/**
 * A file size, in the unit a person would say out loud: `842 KB`, `4.1 MB`.
 *
 * Binary units (1 KB = 1024 B), because that is what the upload limits are expressed in — a 10 MB limit
 * that rejected a file the operating system calls 9.8 MB would be a bug report every time.
 */
export const formatBytes = (value: number | null | undefined): string => {
  if (isBlank(value)) {
    return EM_DASH;
  }

  const bytes = value as number;

  if (bytes < 1024) {
    return `${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(bytes)} B`;
  }

  const units = ['KB', 'MB', 'GB'] as const;
  let size = bytes / 1024;
  let unit = 0;

  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }

  return `${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: size < 10 ? 1 : 0 }).format(size)} ${units[unit]}`;
};

/** A plain integer count with locale grouping, e.g. `1,600`. */
export const formatCount = (value: number | null | undefined): string =>
  isBlank(value) ? EM_DASH : new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(value as number);

/** A decimal number with locale grouping, e.g. `12.5`. Trailing zeros are dropped. */
export const formatDecimal = (value: number | null | undefined, maximumFractionDigits = 2): string =>
  isBlank(value) ? EM_DASH : new Intl.NumberFormat(currentLocale(), { maximumFractionDigits }).format(value as number);

/** Falls back to an em dash so an empty field never renders as an empty cell. */
export const formatText = (value: string | null | undefined): string =>
  value === null || value === undefined || value.trim() === '' ? EM_DASH : value;

/**
 * "Not recorded" — the phrase for a fact nobody has entered yet. It is deliberately distinct from a
 * value of zero and from "unknown condition", which the valuation treats as its own state (PP AC03).
 */
export const notRecorded = (t: (key: string) => string): string => t('common.notRecorded');
